<script setup lang="ts">
import { computed } from 'vue'

import { useInsightActionSelection } from '../composables/useInsightActionSelection'
import { useReasonText } from '../composables/useReasonText'
import type { InsightActionRow } from '../models/insightAction.types'
import { humanizeKey } from '../service/labels'
import { useInsightActionStore } from '../stores/insightActionStore'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import type { UiTone } from '@/shared/ui/types'

defineEmits<{ 'retry-readiness': [] }>()

const store = useInsightActionStore()
const selection = useInsightActionSelection()
const { t } = useI18n()
const format = useFormat()
const { reason, reasonLabel } = useReasonText()

const columnKeys = computed(() => selection.columnKeys.value)
const rows = computed(() => selection.rows.value)

function cell(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  return typeof value === 'number' ? format.formatNumber(value, { maxFrac: 2 }) : String(value)
}

function columnLabel(key: string): string {
  return humanizeKey(key)
}

function rowTitle(row: InsightActionRow): string {
  if (row.summary) return row.summary
  const first = columnKeys.value
    .map((key) => row.columns?.[key])
    .find((value) => value !== null && value !== undefined && value !== '')
  return first === undefined ? '—' : String(first)
}

interface RowBadge {
  text: string
  tone: UiTone
  title: string
}

function alreadyRequestedText(row: InsightActionRow): string {
  const releasedAt = row.eligibility?.releasedAt
  return releasedAt
    ? t('insightActions.select.alreadyRequestedFrees', { time: format.formatDateTime(releasedAt) })
    : t('insightActions.select.alreadyRequestedUntil')
}

function badge(row: InsightActionRow): RowBadge | null {
  const eligibility = row.eligibility
  if (!selection.isRowPickable(row)) {
    const code = eligibility?.state === 'blocked' ? eligibility.reasonCode : row.reasonCode
    const text =
      code === 'already_requested'
        ? alreadyRequestedText(row)
        : reasonLabel(code, channelName(row.channelKey))
    return { text, tone: 'neutral', title: eligibility?.message || reason(code).description }
  }
  if (eligibility?.state === 'pending') {
    return { text: t('insightActions.select.checking'), tone: 'info', title: eligibility.message }
  }
  if (eligibility?.state === 'unknown') {
    return {
      text: t('insightActions.select.notCertain'),
      tone: 'warning',
      title: eligibility.message,
    }
  }
  return null
}

function channelName(key: string | null): string {
  return humanizeKey(key)
}

function rowLabel(row: InsightActionRow): string {
  const marker = badge(row)
  if (!marker || selection.isRowPickable(row)) return rowTitle(row)
  return `${rowTitle(row)} — ${row.eligibility?.message || marker.text}`
}

/** Header cells in grid order: the checkbox, the row summary, then the config's own columns. */
const headers = computed(() => [
  { key: 'select', label: '' },
  { key: 'row', label: t('insightActions.select.rowColumn') },
  ...columnKeys.value.map((key) => ({ key, label: columnLabel(key) })),
])

/** Hover paints per CELL, because a `display: contents` row generates no box of its own. */
function cellClass(row: InsightActionRow): string {
  return selection.isRowPickable(row) ? 'cursor-pointer group-hover:bg-surface-muted' : 'text-muted'
}

const selectAllLabel = computed(() =>
  selection.allSelected.value
    ? t('insightActions.select.clearSelection')
    : t('insightActions.select.selectAll', { count: selection.pickable.value.length }),
)
</script>

<template>
  <!-- ONE grid for the header and every row, with each row `display: contents` so its cells are
       the grid's own children. Two grids cannot be made to line up: each resolves its `1fr`
       against its own content, so a long header over a short cell drifts the columns apart as
       soon as the tracks carry a floor and the table is wider than the drawer. -->
  <div
    class="insight-action-rows max-h-[46vh] overflow-auto rounded-md border border-subtle"
    role="table"
    :aria-label="
      t('insightActions.select.rowsLabel', { label: selection.group.value?.label ?? '' })
    "
    :aria-rowcount="rows.length"
  >
    <!-- `items-stretch`, not `items-center`: the summary cell runs to two lines and the rest to
         one, and a centred cell is only as tall as its own text — so each cell's bottom border
         would sit at a different height and the row would read as a staircase. -->
    <div
      class="grid items-stretch"
      data-testid="row-grid"
      :style="{ gridTemplateColumns: selection.gridTemplate.value }"
    >
      <div role="row" class="contents">
        <span
          v-for="(header, index) in headers"
          :key="header.key"
          role="columnheader"
          class="sticky top-0 z-10 flex items-center border-b border-subtle bg-surface-muted py-xs pe-sm text-start text-2xs font-semibold uppercase tracking-wide text-muted"
          :class="index === 0 ? 'ps-sm' : ''"
        >
          <Checkbox
            v-if="header.key === 'select'"
            :model-value="selection.allSelected.value"
            :indeterminate="selection.someSelected.value && !selection.allSelected.value"
            :disabled="selection.pickable.value.length === 0"
            :aria-label="selectAllLabel"
            data-testid="select-all"
            @update:model-value="selection.toggleAll()"
          />
          <template v-else>{{ header.label }}</template>
        </span>
      </div>

      <div
        v-for="row in rows"
        :key="row.rowKey"
        role="row"
        class="contents group"
        :aria-selected="store.selection.has(row.rowKey)"
      >
        <span
          role="cell"
          class="flex items-center border-b border-subtle py-xs ps-sm pe-sm"
          :class="cellClass(row)"
          @click.stop
        >
          <Checkbox
            :model-value="store.selection.has(row.rowKey)"
            :disabled="!selection.isRowPickable(row)"
            :aria-label="rowLabel(row)"
            @update:model-value="store.toggleRow(row.rowKey)"
          />
        </span>

        <span
          role="cell"
          class="flex min-w-0 flex-col justify-center border-b border-subtle py-xs pe-sm text-sm"
          :class="cellClass(row)"
          @click="selection.isRowPickable(row) && store.toggleRow(row.rowKey)"
        >
          <span class="block truncate" :title="rowTitle(row)">{{ rowTitle(row) }}</span>
          <Badge
            v-if="badge(row)"
            class="mt-2xs self-start"
            :tone="badge(row)!.tone"
            size="sm"
            :title="badge(row)?.title"
            >{{ badge(row)?.text }}</Badge
          >
          <Button
            v-if="row.eligibility?.retryable"
            variant="ghost"
            size="xs"
            data-testid="row-retry"
            :aria-label="t('insightActions.select.retryRow', { row: rowTitle(row) })"
            @click.stop="$emit('retry-readiness')"
          >
            {{ t('insightActions.select.retry') }}
          </Button>
        </span>

        <span
          v-for="key in columnKeys"
          :key="key"
          role="cell"
          class="flex min-w-0 items-center border-b border-subtle py-xs pe-sm text-start text-sm"
          :class="cellClass(row)"
          :title="String(row.columns?.[key] ?? '')"
          @click="selection.isRowPickable(row) && store.toggleRow(row.rowKey)"
        >
          <span class="truncate">{{ cell(row.columns?.[key]) }}</span>
        </span>
      </div>
    </div>

    <p v-if="!rows.length" class="m-0 px-sm py-lg text-center text-xs text-muted">
      {{ t('insightActions.select.noRows') }}
    </p>
  </div>
</template>
